import { ItemPresentingStatusT } from 'types/frontend/shared';
import BaseStore from 'stores/BaseStore';
import { makeAutoObservable, reaction } from 'mobx';
import { debounce } from 'debounce';
import SC from 'constants/shared';
import ClsDataStore from './ClsDataStore';
import { ClsT } from 'types/shared/core/Cls';
import type UserStore from 'stores/root/UserStore';
import WeeklyScheduleInfoHelper from 'utils/shared/WeeklyScheduleInfoHelper';

//
//
//
//
//
//

const dummyClsItemForClear: ClsT = {
  id: 0,
  name: '선택안함',
  permissionRead: 'director',
  permissionWrite: 'director',
  status: 'inOperation',
  assignedStudents: [],
  assignedTeachers: [],
};

export default class ClsWrapperStore implements BaseStore {
  clsDataStore: ClsDataStore;
  userStore?: UserStore;
  visibleClsIdSet: Set<number> | null = null;

  // 보여지거나 선택되었는지
  clsIdAndPresentingStatusMap: Map<number, ItemPresentingStatusT> = new Map();
  filterKeyword = '';
  targetClsStatus: keyof typeof SC.Cls.Status = 'inOperation';
  isClearable = false;
  private customFilteringCriteria:
    | ((clsId: number, filterKeyword: string) => boolean | undefined)
    | null = null;

  get clsItemList() {
    if (this.isClearable) {
      return [dummyClsItemForClear, ...this.clsDataStore.clsItemList];
    } else {
      return this.clsDataStore.clsItemList;
    }
  }

  get selectedItemCount() {
    return Array.from(this.clsIdAndPresentingStatusMap.values()).filter((cls) => cls.selected)
      .length;
  }

  get isLoaded() {
    return this.clsDataStore.isLoaded;
  }

  constructor(clsDataStore: ClsDataStore, isClearable?: boolean, userStore?: UserStore) {
    makeAutoObservable(this);

    this.clsDataStore = clsDataStore;
    this.isClearable = !!isClearable;
    this.userStore = userStore;

    reaction(
      () => this.clsDataStore.clsTimestamp,
      () => this.filterCls(),
    );

    if (this.userStore) {
      reaction(
        () => this.userStore?.myInfoTimestamp,
        () => {
          this.refreshVisibleClsIdSet();
          this.filterCls();
        },
      );
    }
  }

  init = () => {
    console.log('init');
    this.refreshVisibleClsIdSet();
    this.filterCls();
  };

  setCustomFilteringCriteria = (
    criteria?: (clsId: number, filterKeyword: string) => boolean | undefined,
  ) => {
    this.customFilteringCriteria = criteria || null;
    this.filterCls();
  };

  toggleTargetStatus = () => {
    this.targetClsStatus === 'inOperation'
      ? (this.targetClsStatus = 'closed')
      : (this.targetClsStatus = 'inOperation');

    console.log('toggle!', this.targetClsStatus);
    this.filterCls();
  };

  private refreshVisibleClsIdSet = () => {
    if (!this.userStore) {
      this.visibleClsIdSet = null;
      return;
    }
    if (this.userStore.isManager) {
      this.visibleClsIdSet = null;
      return;
    }
    const assignedClsIdList =
      this.userStore.myInfo?.teacherInfo?.assignedClsList.map((cls) => cls.id) || [];
    const nextSet = new Set(assignedClsIdList);
    if (this.isClearable) {
      nextSet.add(dummyClsItemForClear.id);
    }
    this.visibleClsIdSet = nextSet;
  };

  filterEachCls = (cls: ClsT) => {
    const keyword = this.filterKeyword?.toLowerCase();

    const isVisible =
      !this.visibleClsIdSet ||
      this.visibleClsIdSet.has(cls.id) ||
      (this.isClearable && cls.id === dummyClsItemForClear.id);

    if (!isVisible) {
      this.setFiltered(cls.id, false);
      return;
    }

    let filtered = false;
    if (!keyword) filtered = true;
    else if (cls.name.toLowerCase().includes(keyword)) filtered = true;
    else if (cls.assignedTeachers.some((t) => t.nickname.toLowerCase().includes(keyword)))
      filtered = true;
    else if (WeeklyScheduleInfoHelper.matchesClsLessonDayKeyword(cls, this.filterKeyword))
      filtered = true;
    else if (keyword === '배정' && this.clsIdAndPresentingStatusMap.get(cls.id)?.selected)
      filtered = true;
    if (cls.status !== this.targetClsStatus) filtered = false;

    if (typeof this.customFilteringCriteria === 'function') {
      const result = this.customFilteringCriteria(cls.id, this.filterKeyword);
      if (result !== undefined) {
        filtered = result;
      }
    }

    this.setFiltered(cls.id, filtered);
  };

  filterCls = () => {
    console.log('filterCls keyword=', this.filterKeyword);
    for (const cls of this.clsItemList) {
      this.filterEachCls(cls);
    }
  };

  filterClsForDebounce = () => {
    this.filterCls();
  };

  filterClsLazily = debounce(this.filterClsForDebounce, 300);

  setFilterKeyword = (keyword: string) => {
    this.filterKeyword = keyword;
    this.filterClsLazily();
  };

  setFiltered = (clsId: number, filtered: boolean) => {
    const status = this.clsIdAndPresentingStatusMap.get(clsId);

    if (status) {
      status.filtered = filtered;
    } else {
      this.clsIdAndPresentingStatusMap.set(clsId, {
        filtered,
        selected: false,
      });
    }
  };

  handleSelect = (clsId: number) => {
    const status = this.clsIdAndPresentingStatusMap.get(clsId);

    if (status) {
      status.selected = !status.selected;
    } else {
      this.clsIdAndPresentingStatusMap.set(clsId, {
        filtered: true,
        selected: true,
      });
    }
  };

  clearSelection = () => {
    this.clsIdAndPresentingStatusMap.forEach((status) => {
      status.selected = false;
    });
  };

  getSelectedClsList = (): ClsT[] => {
    return Array.from(this.clsIdAndPresentingStatusMap.entries())
      .filter((s) => s[1].selected)
      .map((s) => this.clsDataStore.clsMap.get(s[0]) as ClsT);
  };
}
